/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import RefreshIcon from '@mui/icons-material/Refresh';
import ScheduleIcon from '@mui/icons-material/Schedule';
import {
  Box,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  Link as MuiLink,
  Stack,
  Typography,
} from '@mui/material';
import {
  type AbsoluteURL,
  coreServiceToFlow,
  FLOW_RUNNING_STATUSES,
  FlowRunState,
  type FlowRunStatus,
  gisFlows,
  paBatchFlows,
  paFlows,
  plantSimFlows,
  type WorkflowMeta,
} from '@poweralpha/common';
import dayjs from 'dayjs';
import { sum } from 'lodash';
import { useEffect, useEffectEvent, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useMultipleWorkflowResultsQuery } from '../../react-components/queries';
import { InfoPill } from '../../react-components/text';
import Widget from '../../react-components/Widget';
import { useSimulationInfiniteQuery } from '../../simulation-status/api';
import { parseProjectIdFromTags } from '../../simulation-status/utils';

const RUN_STATUS_LIMIT = 200;
const RECENT_COMPLETED_LIMIT = 10;

const simulationFlows = new Set([
  ...paFlows,
  ...paBatchFlows,
  ...plantSimFlows,
  ...gisFlows,
  ...Object.values(coreServiceToFlow),
]);
const projectBatchFlows = new Set([...paBatchFlows, ...paFlows, ...gisFlows]);
const plantFlows = new Set(plantSimFlows);

const formatDateTime = (value: string | null | undefined) => {
  if (!value) return '-';
  const date = dayjs(value);
  return date.isValid() ? date.format('MM/DD/YYYY hh:mm A') : '-';
};

const byCreatedDesc = (a: FlowRunStatus, b: FlowRunStatus) =>
  dayjs(b.created).valueOf() - dayjs(a.created).valueOf();

const getCompletedRunLink = (run: FlowRunStatus): AbsoluteURL | null => {
  if (projectBatchFlows.has(run.flow_name)) {
    const projectId = parseProjectIdFromTags(run.tags);
    if (!projectId) return '/projects';
    if (paBatchFlows.includes(run.flow_name as (typeof paBatchFlows)[number])) {
      return `/projects/${projectId}/batch/${run.id}/`;
    }
    return `/projects/${projectId}/batch`;
  }

  if (plantFlows.has(run.flow_name)) {
    switch (run.flow_name) {
      case 'PlantSim Batch':
        return '/plant/batch/results';
      case 'PlantSim Net Benefit':
        return '/plant/net-benefit';
      default:
        return '/plant';
    }
  }

  return null;
};

function RunningWorkflows() {
  const runsQuery = useSimulationInfiniteQuery();
  const loadedRunCount = sum(runsQuery.data?.pages.flatMap((page) => page.rows.length));
  const totalRunCount = runsQuery.data?.pages.at(-1)?.count ?? 0;
  const canFetchMore = loadedRunCount < totalRunCount && loadedRunCount < RUN_STATUS_LIMIT;

  const runs = useMemo(
    () =>
      (runsQuery.data?.pages.flatMap((page) => page.rows) ?? [])
        .filter((run) => simulationFlows.has(run.flow_name))
        .sort(byCreatedDesc),
    [runsQuery.data?.pages],
  );

  const runningRuns = useMemo(
    () => runs.filter((run) => FLOW_RUNNING_STATUSES.includes(run.state.type)),
    [runs],
  );
  const completedRuns = useMemo(
    () =>
      runs
        .filter((run) => run.state.type === FlowRunState.COMPLETED)
        .slice(0, RECENT_COMPLETED_LIMIT),
    [runs],
  );
  const displayRunIds = useMemo(
    () => [...runningRuns.map((run) => run.id), ...completedRuns.map((run) => run.id)],
    [completedRuns, runningRuns],
  );
  const runningWorkflowQuery = useMultipleWorkflowResultsQuery<WorkflowMeta>(displayRunIds, {
    enabled: displayRunIds.length > 0,
  });

  const numberOfRunsByRunId = useMemo(
    () =>
      new Map(
        (runningWorkflowQuery.data ?? []).flatMap((workflow) => {
          const total = workflow.meta?.number_of_runs;
          if (total === null || total === undefined) return [];
          return [[workflow.id, total] as const];
        }),
      ),
    [runningWorkflowQuery.data],
  );

  const progressByRunId = useMemo(
    () =>
      new Map(
        (runningWorkflowQuery.data ?? []).flatMap((workflow) => {
          const total = workflow.meta?.number_of_runs;
          if (!total || total <= 0) return [];
          const finished = workflow.meta?.finished_runs ?? 0;
          return [[workflow.id, Math.min(100, (finished / total) * 100)] as const];
        }),
      ),
    [runningWorkflowQuery.data],
  );

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: not that bad
  const renderRunItem = (run: FlowRunStatus) => {
    const isRunning = FLOW_RUNNING_STATUSES.includes(run.state.type);
    const completedRunLink = getCompletedRunLink(run);
    const progress = progressByRunId.get(run.id);
    const numberOfRuns = numberOfRunsByRunId.get(run.id);
    const hasDeterminateProgress = progress !== undefined && progress > 0 && progress < 100;
    const timeLabel = isRunning ? 'Started' : 'Completed';
    const timeValue = formatDateTime(
      isRunning ? run.start_time : (run.end_time ?? run.updated ?? run.created),
    );
    const rowSx = {
      borderRadius: 1,
      py: 0.75,
      px: 1.5,
      minHeight: 62,
      alignItems: 'flex-start',
    };

    return (
      <ListItem key={run.id} disablePadding>
        <ListItemButton
          component={completedRunLink ? RouterLink : 'div'}
          to={completedRunLink ?? undefined}
          sx={rowSx}
        >
          <Stack
            spacing={0.5}
            sx={{
              width: '100%',
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Stack
                direction="row"
                spacing={0.75}
                sx={{
                  alignItems: 'baseline',
                  minWidth: 0,
                }}
              >
                <MuiLink
                  component="span"
                  underline="always"
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    lineHeight: 1.2,
                    color: 'primary.main',
                  }}
                  noWrap
                >
                  {run.name || 'Untitled run'}
                </MuiLink>
                {numberOfRuns !== undefined && (
                  <Typography
                    noWrap
                    sx={{
                      color: 'text.secondary',
                      fontSize: 13,
                    }}
                  >
                    • {numberOfRuns} runs
                  </Typography>
                )}
              </Stack>
              <InfoPill label={run.flow_name} height={18} />
            </Stack>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Stack
                direction="row"
                spacing={0.75}
                sx={{
                  alignItems: 'center',
                  minWidth: 0,
                }}
              >
                <ScheduleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography
                  variant="caption"
                  noWrap
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {timeLabel}: {timeValue}
                </Typography>
              </Stack>
            </Stack>
            {isRunning && (
              <LinearProgress
                variant={hasDeterminateProgress ? 'determinate' : 'indeterminate'}
                value={hasDeterminateProgress ? progress : undefined}
                color="secondary"
              />
            )}
          </Stack>
        </ListItemButton>
      </ListItem>
    );
  };

  const fetchMore = useEffectEvent(runsQuery.fetchNextPage);
  useEffect(() => {
    if (runsQuery.isFetching || !canFetchMore) {
      return;
    }
    if (completedRuns.length < RECENT_COMPLETED_LIMIT) {
      fetchMore();
    }
  }, [canFetchMore, completedRuns.length, runsQuery.isFetching]);

  return (
    <Widget
      title="Batch Workflows"
      actions={
        <IconButton
          loading={runsQuery.isRefetching}
          size="small"
          onClick={() => runsQuery.refetch()}
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
      }
    >
      <Box sx={{ maxHeight: 340, overflowY: 'auto' }}>
        {runningRuns.length + completedRuns.length === 0 ? (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              p: 1.5,
              py: 2,
            }}
          >
            No workflows found.
          </Typography>
        ) : (
          <List dense disablePadding>
            {runningRuns.map(renderRunItem)}
            {completedRuns.map(renderRunItem)}
          </List>
        )}
      </Box>
    </Widget>
  );
}

export default RunningWorkflows;
